import { redirect } from 'redux-first-router';
import { identity, isFunction, mapValues } from 'lodash';
import { all, call, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { apiRequest, createCancelSource, isRequestCancelled } from '../../api/core';
import { getAccessToken, getRefreshToken, getUser, isUserAuthorized } from '../selectors/user';
import { SEND_REQUEST, SEND_REQUEST_FAIL, SEND_REQUEST_SUCCESS } from '../actions/actionTypes/request';
import { REQUEST_TYPES } from '../../api/requests/requestTypes';
import { REQUESTS } from '../../api/requests/request';
import { timestamp } from '../../utils/common';
import { errorCodes } from '../../utils/errorCodes';
import { ACCESS_TOKEN_REFRESHED, LOGOUT } from '../actions/actionTypes/user';
import { loginWithToken, logout } from '../actions/user';
import { DASHBOARD_TABS, USER_ROLES } from '../../utils/constants';
import {
    APP_INITIALIZATION_FAIL,
    APP_INITIALIZATION_START,
    APP_INITIALIZATION_SUCCESS,
    REHYDRATION_DONE
} from '../actions/actionTypes/common';
import { initCardsFilters } from '../actions/filters';
import { LockFirstAndQueue } from './utils/LockFirstAndQueue';
import { fetchCommonData, fetchTeams } from '../actions/common';
import { captureException } from '../../tracker/exceptionTracker';
import { getRouterLocation } from '../selectors/common';
import { PAGES } from '../../utils/pages';
import { getTokenValidity } from '../../utils/token';
import { unsetUser } from '../../tracker/MatomoAnalytics';
import { queryClient } from '../../api/queryClient';
import { fetchConfiguration, fetchDataPrivacy } from '../actions/settings';
import { BAD_REQUEST, UNAUTHORIZED } from '../../api/requests/requestStatusConstants';

import { BAD_TOKEN, INVALID_GRANT, TOKEN_EXPIRED } from '../../api/requests/requestMessageConstants';
import { ROUTES } from '../../utils/routes';
import { type RootState } from '../types';
import { type AnyAction } from 'redux';

import {
    type ApiRequestDescriptor,
    type ApiResponse,
    type NormalizedError,
    type RequestHandler
} from '../../api/types';

const noop = () => {};

const safeSaga = (fn: (action: AnyAction) => Generator<any, void, any>) =>
    function* callSagaSafely(action: AnyAction): Generator<any, void, any> {
        try {
            yield call(fn, action);
        } catch (error) {
            console.error(error);
            captureException(error);
        }
    };

export function sendRequestSuccess(data: any, request: any, meta: any): AnyAction {
    return {
        type: SEND_REQUEST_SUCCESS,
        payload: {
            data
        },
        request: { ...request, timestamp: timestamp() },
        meta
    };
}

export function sendRequestFail({ request, error, meta }: { request: any; error: any; meta: any }): AnyAction {
    return {
        type: SEND_REQUEST_FAIL,
        payload: {},
        request,
        meta: {
            ...meta,
            error
        }
    };
}

export function* makeSingleApiRequest(
    descriptor: ApiRequestDescriptor,
    accessToken: string | null,
    cancelToken: any
): Generator<any, any, any> {
    return yield call(apiRequest, { ...descriptor, accessToken, cancelToken });
}

function _makeParallelApiRequests(
    descriptors: Record<string, ApiRequestDescriptor>,
    accessToken: string | null,
    cancelToken: any
) {
    return mapValues(descriptors, descriptor => {
        return call(makeSingleApiRequest, descriptor, accessToken, cancelToken);
    });
}

export function* subRequest(
    descriptor: ApiRequestDescriptor,
    accessToken: string | null,
    cancelToken: any
): Generator<any, any, any> {
    const response = yield call(makeSingleApiRequest, descriptor, accessToken, cancelToken);

    if (descriptor.dependents) {
        const dependents = descriptor.dependents();
        const depResponses = yield all(_makeParallelApiRequests(dependents.descriptors, accessToken, cancelToken));

        return dependents.transformResponse(depResponses, response);
    }

    return response;
}

export function makeParallelApiRequests(
    descriptors: Record<string, ApiRequestDescriptor>,
    accessToken: string | null,
    cancelToken: any
) {
    return mapValues(descriptors, descriptor => call(subRequest, descriptor, accessToken, cancelToken));
}

function getErrorCode(errorsMap: any, error: Partial<NormalizedError>): number {
    const errorData = error.data;
    if (
        (error.status === UNAUTHORIZED &&
            (errorData?.message?.includes(BAD_TOKEN) || errorData?.exp === TOKEN_EXPIRED)) ||
        errorData?.error?.toLowerCase() === TOKEN_EXPIRED
    ) {
        return errorCodes.auth.ACCESS_TOKEN_EXPIRED;
    }

    if (error.status === BAD_REQUEST && errorData?.error === INVALID_GRANT) {
        return errorCodes.auth.REFRESH_TOKEN_EXPIRED;
    }

    if (isFunction(errorsMap)) {
        return errorsMap(error) || errorCodes.REQUEST_ERROR;
    }

    return (error.status && errorsMap?.[error.status]) || errorsMap?.default || errorCodes.REQUEST_ERROR;
}

function normalizeError(errorsMap: any, error: any): NormalizedError {
    const response = error?.response ?? {};
    const { status = 0, data = {} } = response;
    const normalized: NormalizedError = {
        isApiError: error.isAxiosError || false,
        data,
        status,
        stack: error.stack || '',
        message: error.message || ''
    };

    const code = getErrorCode(errorsMap, normalized);

    return { ...normalized, code };
}

export function* makeCompoundRequest(action: AnyAction, cancelToken: any): Generator<any, ApiResponse, any> {
    const { request = {}, payload = {} } = action;
    let handler: RequestHandler | null = null;
    try {
        const factory = REQUESTS[request.type];
        if (!factory) {
            throw new Error(`Request factory for ${request.type} not found`);
        }

        handler = factory(request.params, payload.data);
    } catch (error) {
        console.error(error);
        captureException(error);
        return { error };
    }

    const { descriptors, errors = {} } = handler;
    const transformResponse = handler.transformResponse ?? identity;
    try {
        const accessToken: string | null = yield select(getAccessToken);
        const response: any = yield all(makeParallelApiRequests(descriptors, accessToken, cancelToken));
        const data = transformResponse(response);
        return { data };
    } catch (error) {
        if (isRequestCancelled(error)) {
            return { error };
        }
        console.error('Compound request failed', error);
        return { error: normalizeError(errors, error) };
    }
}

let lock = new LockFirstAndQueue();

export const resetLock = () => {
    lock = new LockFirstAndQueue();
};

export async function makeRefreshTokenRequest(refreshToken: string): Promise<any> {
    const fn = async () => {
        const handler = REQUESTS[REQUEST_TYPES.REFRESH_TOKEN](null, { refreshToken });
        const { descriptors, errors = {} } = handler;
        try {
            const response = await apiRequest(descriptors.refresh);
            return {
                effect: put({
                    type: ACCESS_TOKEN_REFRESHED,
                    payload: { data: response.data }
                }),
                error: false
            };
        } catch (error) {
            return {
                effect: put(logout()),
                error: normalizeError(errors, error)
            };
        }
    };

    const transformError = ({ error }: any) => ({ error });
    return await lock.dispatch(fn, transformError).catch(error => error);
}

const tokens = new Map();

export const resetTokens = () => {
    tokens.clear();
};

export function* doSendRequest(action: AnyAction): Generator<any, void, any> {
    const { request = {}, meta = {}, options = {} } = action;
    const { afterSuccess = noop, afterFail = noop, afterAll = noop } = options;
    const source = createCancelSource();
    const cancelToken = source.token;

    if (tokens.has(request.type)) {
        const src = tokens.get(request.type);
        src.cancel();
    }

    tokens.set(request.type, source);

    const result: ApiResponse = yield call(makeCompoundRequest, action, cancelToken);

    if (isRequestCancelled(result?.error)) {
        return;
    }

    if (result?.error?.code === errorCodes.auth.ACCESS_TOKEN_EXPIRED) {
        const refreshToken: string | null = yield select(getRefreshToken);

        if (!refreshToken) {
            yield put(logout());
        } else {
            const refreshResult: any = yield call(makeRefreshTokenRequest, refreshToken);

            if (refreshResult.error) {
                yield put(sendRequestFail({ request, meta, error: result.error }));
                afterFail(result.error);
            }

            if (refreshResult.effect) {
                yield refreshResult.effect;
            }

            if (!refreshResult.error) {
                const { data, error }: ApiResponse = yield call(makeCompoundRequest, action, cancelToken);
                if (data) {
                    yield put(sendRequestSuccess(data, request, meta));
                    afterSuccess();
                } else {
                    yield put(sendRequestFail({ request, meta, error }));
                    afterFail(error);
                }
            }
        }
    } else if (result.error) {
        const { error } = result;
        captureException(result.error);
        yield put(sendRequestFail({ request, meta, error }));
        afterFail(error);
    } else if (result.data) {
        yield put(sendRequestSuccess(result.data, request, meta));
        afterSuccess();
    }
    tokens.delete(request.type);
    afterAll();
}

export const safeDoSendRequest = safeSaga(doSendRequest);

export function* handleRequest(): Generator<any, void, any> {
    yield takeEvery(SEND_REQUEST, safeDoSendRequest);
}

const requestTypePattern = (type: string) => (action: AnyAction) => action?.request?.type === type;
const requestPhasePattern = (type: string) => (action: AnyAction) => action.type === type;
const requestPattern = (reqType: string, phaseType: string) => (action: AnyAction) =>
    requestTypePattern(reqType)(action) && requestPhasePattern(phaseType)(action);

function* requestSettled(request: string, throwErrorOnFail: boolean): Generator<any, any, any> {
    const { success, fail } = yield race({
        success: take(requestPattern(request, SEND_REQUEST_SUCCESS)),
        fail: take(requestPattern(request, SEND_REQUEST_FAIL))
    });

    if (fail) {
        if (throwErrorOnFail) {
            throw new Error(`Request ${request} failed`);
        }
        return fail;
    }

    return success;
}

function* allRequestSettled(requests: string[], throwErrorOnfail = true): Generator<any, any, any> {
    return yield all(requests.map(request => call(requestSettled, request, throwErrorOnfail)));
}

export function* fetchInitialAppData(logoutOnFail = false): Generator<any, void, any> {
    try {
        yield put({ type: APP_INITIALIZATION_START });
        const user = yield select(getUser);

        const requests: Array<[any, string]> = [];
        if (user.role !== USER_ROLES.CORE_AGENT) {
            requests.push([fetchCommonData(user), REQUEST_TYPES.GET_COMMON_DATA]);
        }

        const awaiting: string[] = [];
        for (let i = 0; i < requests.length; i++) {
            const [action, reqType] = requests[i];
            yield put(action);
            awaiting.push(reqType);
        }

        yield allRequestSettled(awaiting);

        if (user.role !== USER_ROLES.CORE_AGENT) {
            yield put(fetchTeams());
            yield allRequestSettled([REQUEST_TYPES.GET_TEAMS_LIST]);
        }

        yield put({ type: APP_INITIALIZATION_SUCCESS });
        yield put(initCardsFilters());

        yield put(fetchDataPrivacy());
    } catch (error) {
        captureException(error);
        yield put({ type: APP_INITIALIZATION_FAIL });
        if (logoutOnFail) {
            yield put(logout());
        }
    }
}

export function* onAppStart(): Generator<any, void, any> {
    yield take(REHYDRATION_DONE);
    const isAuthorized: boolean = yield select(isUserAuthorized);
    const accessToken: string | null = yield select(getAccessToken);
    const isAccessTokenValid: boolean = yield accessToken ? !getTokenValidity(accessToken) : false;
    const isRefreshTokenAvailable: boolean = yield select((state: RootState) => Boolean(getRefreshToken(state)));
    const location: any = yield select(getRouterLocation);
    const user = yield select(getUser);
    const loginFromCoach = location.type === PAGES.LOGIN && location.query && location.query.token;

    if (loginFromCoach) {
        yield put(logout());
        yield put(loginWithToken(location.query.token));
        yield call(fetchInitialAppData, true);
        return;
    }

    if (isAuthorized && (isRefreshTokenAvailable || isAccessTokenValid)) {
        yield call(fetchInitialAppData, !user.role);
    } else if (location.type !== PAGES.RECOVERY) {
        yield put(logout());
    }
}

export function* doRedirectOnLogin(): Generator<any, void, any> {
    yield put(fetchConfiguration());
    yield allRequestSettled([REQUEST_TYPES.GET_CONFIG]);

    const location: any = yield select(getRouterLocation);

    const loginRedirect = location?.prev?.query?.redirect;
    if (loginRedirect) {
        const url = new URL(`${document.location.protocol}//${document.location.host}${loginRedirect}`);

        const routeIndex = Object.values(ROUTES).findIndex((r: any) => {
            return r.path && r.path.indexOf(url.pathname) === 0;
        });

        if (routeIndex > -1) {
            const page = Object.keys(ROUTES)[routeIndex];
            yield put(
                redirect({
                    type: page,
                    payload: { hash: url.hash ? url.hash.substring(1) : undefined }
                })
            );

            yield call(fetchInitialAppData);
            return;
        }
    }

    if (location.kind === 'redirect' && location.prev.type !== '' && location.pathname !== location.prev.pathname) {
        yield put(
            redirect({
                type: location.prev.type,
                payload: location.prev.payload,
                meta: {
                    location: location.prev,
                    query: location.prev.query
                }
            })
        );
    } else {
        yield put(
            redirect({
                type: PAGES.DASHBOARD,
                payload: { section: DASHBOARD_TABS.i2x }
            })
        );
    }

    yield call(fetchInitialAppData);
}

export function* onLogin(): Generator<any, void, any> {
    yield takeLatest(requestPattern(REQUEST_TYPES.LOGIN, SEND_REQUEST_SUCCESS), doRedirectOnLogin);
}

export function* logoutSideEffect(): Generator<any, void, any> {
    unsetUser();

    // Runs on every LOGOUT dispatch (manual button or the forced 401/403
    // redirect in index.tsx's query retry), so the next login in the same tab
    // never reuses the previous user's React Query cache or filter state.
    queryClient.clear();

    Object.keys(localStorage).forEach(key => {
        if (!key.startsWith('keep.')) {
            localStorage.removeItem(key);
        }
    });

    // Just to satisfy generator
    yield true;
}

export function* onLogout(): Generator<any, void, any> {
    yield takeLatest(LOGOUT, logoutSideEffect);
}

export const requestAndAuthFlowHandler = [onAppStart(), handleRequest(), onLogin(), onLogout()];
